// M15 (share-loop) — compact Copy link / Email / LinkedIn / Slack row.
// Rendered on the owner's report page (under the Share button, once a
// link has been minted this session) and on the public share viewer
// (always — the viewer's own URL is the share URL). No SDKs:
//
//   - Copy link  → clipboard write, label flips to "Copied".
//   - Email      → mailto: with the prewritten boss framing (no-stat
//                  variant — this surface has no report payload).
//   - LinkedIn   → share-offsite URL in a new tab.
//   - Slack      → there is no Slack share URL, so we copy a one-line
//                  message + URL FIRST (inside the user gesture, so the
//                  clipboard write is allowed) and then best-effort hop
//                  to the Slack app via slack://open. Label flips to
//                  "Copied. Paste it in Slack" either way.
//
// Every click fires `share_button_clicked` with {channel, surface} via
// both reportTelemetry (Workers Analytics Engine — accepted without a
// session, the viewer is anonymous) and pushGtmEvent (GA4/HubSpot
// fan-out). See docs/gtm-events.md for the event catalogue row.

import { useEffect, useRef, useState } from "react";
import { buildFramedMessage, framedMessageToMailtoHref } from "../../../shared/share/framing";
import { pushGtmEvent } from "../../lib/gtm-events";
import { reportTelemetry } from "../../lib/telemetry";

export type ShareButtonsSurface = "report" | "share_viewer";

type ShareChannel = "copy" | "email" | "linkedin" | "slack";

export interface ShareButtonsRowProps {
  /** Absolute share URL — the minted link on /app/report, the page's own
   *  URL on the share viewer. */
  shareUrl: string;
  /** Telemetry surface tag. */
  surface: ShareButtonsSurface;
  /** Property display name for the email subject line. */
  propertyName: string;
}

const LINKEDIN_SHARE_BASE = "https://www.linkedin.com/sharing/share-offsite/?url=";

export function ShareButtonsRow({ shareUrl, surface, propertyName }: ShareButtonsRowProps) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedSlack, setCopiedSlack] = useState(false);
  // Track timers so a fast unmount (route change) doesn't setState on a
  // dead component.
  const timersRef = useRef<number[]>([]);
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, []);

  const fireClicked = (channel: ShareChannel) => {
    reportTelemetry({ event: "share_button_clicked", tags: { channel, surface } });
    pushGtmEvent("share_button_clicked", { channel, surface });
  };

  const flashState = (set: (v: boolean) => void) => {
    set(true);
    timersRef.current.push(window.setTimeout(() => set(false), 2400));
  };

  const handleCopyLink = async () => {
    fireClicked("copy");
    try {
      await navigator.clipboard.writeText(shareUrl);
      flashState(setCopiedLink);
    } catch {
      // Clipboard can be blocked (permissions / non-secure context) —
      // nothing to surface, the share URL is visible elsewhere on both
      // host surfaces.
    }
  };

  const handleSlack = async () => {
    fireClicked("slack");
    // Clipboard FIRST — it must happen inside the click gesture or
    // browsers reject the write once we navigate to the slack:// scheme.
    const oneLiner = `Social traffic report for ${propertyName}. No login needed: ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(oneLiner);
      flashState(setCopiedSlack);
    } catch {
      // Same silent-failure contract as Copy link.
    }
    try {
      // Best-effort app hop. Browsers without a slack: handler show
      // nothing or a dismissible prompt; the copied message is the real
      // payload either way.
      window.location.href = "slack://open";
    } catch {
      // Some browsers throw on unknown schemes — the copy already
      // happened, so there's nothing left to do.
    }
  };

  const mailtoHref = framedMessageToMailtoHref(
    buildFramedMessage("boss", { propertyName, url: shareUrl }),
  );

  const pillClass =
    "inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 ring-1 ring-neutral-950/10 transition-colors hover:bg-neutral-50 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2";

  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Share this report">
      <button type="button" onClick={handleCopyLink} className={pillClass} aria-live="polite">
        <i
          className={`fa-regular ${copiedLink ? "fa-check text-emerald-600" : "fa-copy text-purple-600"} text-[11px]`}
          aria-hidden="true"
        />
        {copiedLink ? "Copied" : "Copy link"}
      </button>
      <a href={mailtoHref} onClick={() => fireClicked("email")} className={pillClass}>
        <i className="fa-regular fa-envelope text-[11px] text-purple-600" aria-hidden="true" />
        Email
      </a>
      <a
        href={`${LINKEDIN_SHARE_BASE}${encodeURIComponent(shareUrl)}`}
        target="_blank"
        rel="noreferrer"
        onClick={() => fireClicked("linkedin")}
        className={pillClass}
      >
        <i className="fa-brands fa-linkedin-in text-[11px] text-purple-600" aria-hidden="true" />
        LinkedIn
      </a>
      <button type="button" onClick={handleSlack} className={pillClass} aria-live="polite">
        <i
          className={`${copiedSlack ? "fa-regular fa-check text-emerald-600" : "fa-brands fa-slack text-purple-600"} text-[11px]`}
          aria-hidden="true"
        />
        {copiedSlack ? "Copied. Paste it in Slack" : "Slack"}
      </button>
    </div>
  );
}
